import React, { createContext, useEffect, useState } from 'react';
import { API_BASE } from '../config/api';

export interface User {
    id: number;
    username: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    isAdmin?: boolean;
    avatarVersion: number;
}

interface MeApiResponse {
    user_id: number;
    username: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    is_admin?: boolean;
}

interface ActionResult {
    ok: boolean;
    error?: string;
}

export interface RegisterPayload {
    username: string;
    password: string;
    firstName?: string;
    lastName?: string;
    email?: string;
}

export interface UpdateUserPayload {
    displayName?: string;
    username?: string;
    email?: string;
    newPassword?: string;
}

interface UserContextType {
    user: User | null;
    loading: boolean;
    /** Whether a new account can still be created (first-user bootstrap). */
    registrationOpen: boolean;
    /** True until the registration status has been fetched once. */
    registrationLoading: boolean;
    login: (usernameOrEmail: string, password: string) => Promise<ActionResult>;
    register: (payload: RegisterPayload) => Promise<ActionResult>;
    updateUser: (payload: UpdateUserPayload) => Promise<ActionResult>;
    uploadAvatar: (file: File) => Promise<ActionResult>;
    logout: () => Promise<void>;
}

function mapUserResponse(data: MeApiResponse, previousAvatarVersion = 0): User {
    return {
        id: data.user_id,
        username: data.username,
        firstName: data.first_name,
        lastName: data.last_name,
        email: data.email,
        isAdmin: data.is_admin,
        avatarVersion: previousAvatarVersion,
    };
}

async function parseErrorMessage(response: Response): Promise<string> {
    try {
        const data = await response.json();
        return data.message || response.statusText;
    } catch {
        return response.statusText;
    }
}

export const UserContext = createContext<UserContextType>({
    user: null,
    loading: true,
    registrationOpen: false,
    registrationLoading: true,
    login: async () => ({ ok: false, error: 'Not implemented' }),
    register: async () => ({ ok: false, error: 'Not implemented' }),
    updateUser: async () => ({ ok: false, error: 'Not implemented' }),
    uploadAvatar: async () => ({ ok: false, error: 'Not implemented' }),
    logout: async () => {},
});

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [registrationOpen, setRegistrationOpen] = useState<boolean>(false);
    const [registrationLoading, setRegistrationLoading] = useState<boolean>(true);

    useEffect(() => {
        (async () => {
            try {
                const response = await fetch(`${API_BASE}/api/register/status`, {
                    credentials: 'include',
                });
                if (response.ok) {
                    const data: { open?: boolean } = await response.json();
                    setRegistrationOpen(Boolean(data.open));
                } else {
                    // Fail closed: never advertise a register form the
                    // server would reject with 403 anyway.
                    setRegistrationOpen(false);
                }
            } catch {
                setRegistrationOpen(false);
            } finally {
                setRegistrationLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const response = await fetch(`${API_BASE}/api/me`, {
                    credentials: 'include',
                });
                if (response.ok) {
                    const data: MeApiResponse = await response.json();
                    setUser(mapUserResponse(data));
                } else {
                    setUser(null);
                }
            } catch {
                setUser(null);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const login = async (usernameOrEmail: string, password: string): Promise<ActionResult> => {
        try {
            const response = await fetch(`${API_BASE}/api/login`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: usernameOrEmail, password }),
            });
            if (!response.ok) {
                return { ok: false, error: await parseErrorMessage(response) };
            }
            const data: MeApiResponse = await response.json();
            setUser((prev) => mapUserResponse(data, prev?.avatarVersion));
            return { ok: true };
        } catch (error) {
            return { ok: false, error: 'Could not reach the server' };
        }
    };

    const register = async (payload: RegisterPayload): Promise<ActionResult> => {
        try {
            const response = await fetch(`${API_BASE}/api/register`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: payload.username,
                    password: payload.password,
                    first_name: payload.firstName,
                    last_name: payload.lastName,
                    email: payload.email,
                }),
            });
            if (!response.ok) {
                return { ok: false, error: await parseErrorMessage(response) };
            }
            const data: MeApiResponse = await response.json();
            setUser((prev) => mapUserResponse(data, prev?.avatarVersion));
            // This account was the first user, so the door just closed
            // behind it - no extra round trip needed to know that.
            setRegistrationOpen(false);
            return { ok: true };
        } catch (error) {
            return { ok: false, error: 'Could not reach the server' };
        }
    };

    const updateUser = async (payload: UpdateUserPayload): Promise<ActionResult> => {
        try {
            const response = await fetch(`${API_BASE}/api/me`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    display_name: payload.displayName,
                    username: payload.username,
                    email: payload.email,
                    new_password: payload.newPassword,
                }),
            });
            if (!response.ok) {
                return { ok: false, error: await parseErrorMessage(response) };
            }
            const data: MeApiResponse = await response.json();
            setUser((prev) => mapUserResponse(data, prev?.avatarVersion));
            return { ok: true };
        } catch (error) {
            return { ok: false, error: 'Could not reach the server' };
        }
    };

    const uploadAvatar = async (file: File): Promise<ActionResult> => {
        try {
            const formData = new FormData();
            formData.append('avatar', file);
            const response = await fetch(`${API_BASE}/api/me/avatar`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });
            if (!response.ok) {
                return { ok: false, error: await parseErrorMessage(response) };
            }
            setUser((prev) => (prev ? { ...prev, avatarVersion: prev.avatarVersion + 1 } : prev));
            return { ok: true };
        } catch (error) {
            return { ok: false, error: 'Could not reach the server' };
        }
    };

    const logout = async (): Promise<void> => {
        try {
            await fetch(`${API_BASE}/api/logout`, {
                method: 'POST',
                credentials: 'include',
            });
        } finally {
            setUser(null);
        }
    };

    return (
        <UserContext.Provider
            value={{
                user,
                loading,
                registrationOpen,
                registrationLoading,
                login,
                register,
                updateUser,
                uploadAvatar,
                logout,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};
